import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { useNotifications } from '../hooks/useNotifications';
import SessionFormFields from './SessionFormFields';
import { sendAgeBasedNotifications } from '../services/sessionNotificationService';
import { validateSessionForm } from '../utils/sessionValidation';
import { useAuth } from '@/contexts/AuthContext';

interface NewSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSessionCreated?: () => void;
}

const NewSessionDialog: React.FC<NewSessionDialogProps> = ({ open, onOpenChange, onSessionCreated }) => {
  useUiLanguage();
  const [title, setTitle] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [description, setDescription] = useState('');
  const [ageGroup, setAgeGroup] = useState<'all' | 'kids' | 'teens' | 'adults'>('all');
  const [sessionType, setSessionType] = useState<'in_person' | 'online' | 'hybrid'>('in_person');
  const [onlineMeetingUrl, setOnlineMeetingUrl] = useState('');
  const [meetingPlatform, setMeetingPlatform] = useState('');
  const [meetingPassword, setMeetingPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { sendNotification } = useNotifications();
  const { profile } = useAuth();

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (value !== 'Custom') {
      setCustomTitle('');
    }
  };

  const getFinalTitle = () => {
    return title === 'Custom' ? customTitle : title;
  };

  const resetForm = () => {
    setTitle('');
    setCustomTitle('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setMaxParticipants('');
    setDescription('');
    setAgeGroup('all');
    setSessionType('in_person');
    setOnlineMeetingUrl('');
    setMeetingPlatform('');
    setMeetingPassword('');
  };

  const handleScheduleSession = async () => {
    const finalTitle = getFinalTitle();

    if (!validateSessionForm(finalTitle, date, startTime, endTime)) {
      return;
    }

    setIsLoading(true);

    try {
      const sessionData = {
        title: finalTitle.trim(),
        date,
        start_time: startTime,
        end_time: endTime,
        max_participants: maxParticipants ? parseInt(maxParticipants) : null,
        description: description.trim() || null,
        age_group: ageGroup,
        session_type: sessionType,
        online_meeting_url: onlineMeetingUrl.trim() || null,
        meeting_platform: meetingPlatform || null,
        meeting_password: meetingPassword.trim() || null,
        dojo_id: profile?.dojo_id!
      };

      let insertedSession;

      if (title === 'Belt Promotion Session') {
        // Insert into promotion_sessions table
        const { data, error } = await supabase
          .from('promotion_sessions')
          .insert([sessionData])
          .select()
          .single();

        if (error) throw error;
        insertedSession = data;

        // Send age-based notifications for promotion session
        await sendAgeBasedNotifications({
          sessionData: { ...insertedSession },
          sessionType: 'promotion_session',
          ageGroup,
          title: finalTitle,
          date,
          startTime,
          endTime,
          description,
          sendNotification
        });
      } else {
        // Insert into training_sessions table
        const { data, error } = await supabase
          .from('training_sessions')
          .insert([sessionData])
          .select()
          .single();

        if (error) throw error;
        insertedSession = data;

        // Send age-based notifications for training session
        await sendAgeBasedNotifications({
          sessionData: { ...insertedSession },
          sessionType: 'training_session',
          ageGroup,
          title: finalTitle,
          date,
          startTime,
          endTime,
          description,
          sendNotification
        });
      }

      const ageGroupText = ageGroup === 'kids' ? uiText(" (Kids 5-12)") : ageGroup === 'teens' ? uiText(" (Teens 13-17)") : ageGroup === 'adults' ? uiText(" (Adults 18+)") : '';

      toast({
        title: uiText("Success"),
        description: uiText("{{value0}} scheduled and notifications sent{{value1}}!", { value0: title === 'Belt Promotion Session' ? uiText("Promotion session") : uiText("Training session"), value1: ageGroupText })
      });

      resetForm();
      onOpenChange(false);
      if (onSessionCreated) {
        onSessionCreated();
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to schedule session or send notifications."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{uiText("Schedule New Session")}</DialogTitle>
          <DialogDescription>
            {uiText("Create a new training session or belt promotion session. Email notifications will be sent automatically based on the selected age group.")}</DialogDescription>
        </DialogHeader>

        <SessionFormFields
          title={title}
          onTitleChange={handleTitleChange}
          customTitle={customTitle}
          setCustomTitle={setCustomTitle}
          ageGroup={ageGroup}
          setAgeGroup={setAgeGroup}
          date={date}
          setDate={setDate}
          maxParticipants={maxParticipants}
          setMaxParticipants={setMaxParticipants}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          description={description}
          setDescription={setDescription}
          sessionType={sessionType}
          setSessionType={setSessionType}
          onlineMeetingUrl={onlineMeetingUrl}
          setOnlineMeetingUrl={setOnlineMeetingUrl}
          meetingPlatform={meetingPlatform}
          setMeetingPlatform={setMeetingPlatform}
          meetingPassword={meetingPassword}
          setMeetingPassword={setMeetingPassword}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {uiText("Cancel")}</Button>
          <Button
            onClick={handleScheduleSession}
            disabled={isLoading}
            className="bg-red-900 hover:bg-red-800"
          >
            {isLoading ? uiText("Scheduling...") : uiText("Schedule Session")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewSessionDialog;
